import { useState } from 'react';
import { theme } from '../../theme.js';
import { useAuth } from '../../context/AuthContext.js';
import {
  useLocations, useCreateLocation, useUpdateLocation, useRetireLocation,
  useLocationEquipment, useSetLocationEquipment, useRemoveLocationEquipment,
  useEquipment,
} from '../../lib/queries.js';
import { Icon } from '../../components/Icon.js';
import type { Location } from '@keetcheck/shared';

/**
 * Storage locations (rooms, warehouse bays…) and what's kept in each, so
 * search can say where equipment is when it isn't on an asset.
 */
export function LocationsPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { data: locations = [], isLoading } = useLocations(orgId);
  const createMut = useCreateLocation(orgId);

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div style={{ flex: 1, background: theme.surface }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', background: theme.bg, borderBottom: `1px solid ${theme.border}`,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: theme.textDim }}>
          {locations.length} location{locations.length !== 1 ? 's' : ''}
        </span>
        <button onClick={() => setShowForm(!showForm)} style={addBtn}>
          <Icon name="plus" size={15} /> Add location
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMut.mutate(
              { name: name.trim(), description: description.trim() || undefined },
              { onSuccess: () => { setName(''); setDescription(''); setShowForm(false); } },
            );
          }}
          style={{
            padding: '12px 16px', background: theme.surface2, borderBottom: `1px solid ${theme.border}`,
            display: 'flex', flexDirection: 'column', gap: 8,
          }}
        >
          <input required value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Name (e.g. Store room, Bay 3)" aria-label="Location name" style={inputStyle} />
          <input value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)" aria-label="Description" style={inputStyle} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setShowForm(false)} style={{ ...smallBtn, flex: 1 }}>Cancel</button>
            <button type="submit" disabled={createMut.isPending} style={{ ...primarySmall, flex: 2 }}>
              {createMut.isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {isLoading && <EmptyMsg>Loading…</EmptyMsg>}
      {!isLoading && locations.length === 0 && (
        <EmptyMsg>No storage locations yet. Add one for equipment that isn’t kept on an asset.</EmptyMsg>
      )}

      {locations.map((loc) => (
        <LocationPanel
          key={loc.id} orgId={orgId} location={loc}
          expanded={expanded === loc.id}
          onToggle={() => setExpanded(expanded === loc.id ? null : loc.id)}
        />
      ))}
    </div>
  );
}

function LocationPanel({
  orgId, location, expanded, onToggle,
}: { orgId: string; location: Location; expanded: boolean; onToggle: () => void }) {
  return (
    <div style={{ borderBottom: `1px solid ${theme.border}` }}>
      <button onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
        padding: '13px 16px', background: theme.surface, border: 'none', cursor: 'pointer',
        fontFamily: theme.font,
      }}>
        <Icon name={expanded ? 'chevronD' : 'chevron'} size={15} style={{ color: theme.textMuted }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{location.name}</div>
          {location.description && (
            <div style={{ fontSize: 12, color: theme.textMuted }}>{location.description}</div>
          )}
        </div>
        <span style={{ fontSize: 12, color: theme.textMuted }}>
          {location.equipmentCount ?? 0} item{location.equipmentCount === 1 ? '' : 's'}
        </span>
      </button>
      {expanded && <LocationDetail orgId={orgId} location={location} />}
    </div>
  );
}

function LocationDetail({ orgId, location }: { orgId: string; location: Location }) {
  const { data: stock = [], isLoading } = useLocationEquipment(orgId, location.id);
  const { data: equipment = [] } = useEquipment(orgId);
  const setMut = useSetLocationEquipment(orgId, location.id);
  const removeMut = useRemoveLocationEquipment(orgId, location.id);
  const updateMut = useUpdateLocation(orgId);
  const retireMut = useRetireLocation(orgId);

  const [equipmentId, setEquipmentId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(location.name);
  const [confirmRetire, setConfirmRetire] = useState(false);

  const selected = equipment.find((e) => e.id === equipmentId);
  const stocked = new Set(stock.map((s) => s.equipmentId));

  return (
    <div style={{ background: theme.surface2, padding: '10px 16px 14px' }}>
      {isLoading && <EmptyMsg>Loading…</EmptyMsg>}
      {!isLoading && stock.length === 0 && <EmptyMsg>Nothing stored here yet.</EmptyMsg>}
      {stock.map((s) => (
        <div key={s.equipmentId} style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
          borderBottom: `1px solid ${theme.border}`,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, color: theme.text, fontWeight: 500 }}>{s.equipmentName}</div>
            <div style={{ fontSize: 12, color: theme.textMuted }}>
              {s.equipmentType === 'tracked' ? (s.serialNumber ? `#${s.serialNumber}` : 'tracked') : `× ${s.quantity}`}
            </div>
          </div>
          <button aria-label="Remove from this location" onClick={() => removeMut.mutate(s.equipmentId)} title="Remove from this location" style={smallBtn}>
            <Icon name="trash" size={12} />
          </button>
        </div>
      ))}

      {/* add / re-count stock */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!equipmentId) return;
          setMut.mutate(
            { equipmentId, quantity: selected?.equipmentType === 'pooled' ? parseInt(quantity, 10) || 1 : undefined },
            { onSuccess: () => { setEquipmentId(''); setQuantity('1'); } },
          );
        }}
        style={{ display: 'flex', gap: 8, marginTop: 10 }}
      >
        <select value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)} required
          aria-label="Equipment to store here" style={{ ...inputStyle, flex: 1 }}>
          <option value="">Store equipment here…</option>
          {equipment.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}{e.serialNumber ? ` #${e.serialNumber}` : ''}{stocked.has(e.id) ? ' (update count)' : ''}
            </option>
          ))}
        </select>
        {selected?.equipmentType === 'pooled' && (
          <input type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)}
            aria-label="Quantity" style={{ ...inputStyle, width: 72 }} />
        )}
        <button type="submit" disabled={setMut.isPending || !equipmentId} style={primarySmall}>
          {stocked.has(equipmentId) ? 'Update' : 'Add'}
        </button>
      </form>

      {/* rename / retire */}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {renaming ? (
          <>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} aria-label="New name"
              style={{ ...inputStyle, flex: 1 }} />
            <button
              onClick={() => updateMut.mutate({ id: location.id, name: newName.trim() }, { onSuccess: () => setRenaming(false) })}
              disabled={!newName.trim()} style={primarySmall}>Save</button>
            <button onClick={() => setRenaming(false)} style={smallBtn}>Cancel</button>
          </>
        ) : (
          <button onClick={() => setRenaming(true)} style={smallBtn}>Rename</button>
        )}
        {!renaming && (confirmRetire ? (
          <>
            <span style={{ fontSize: 12, color: theme.status.danger.fg }}>
              {stock.length ? 'Move or remove everything first.' : 'Retire this location?'}
            </span>
            <button onClick={() => setConfirmRetire(false)} style={smallBtn}>Keep</button>
            {!stock.length && (
              <button onClick={() => retireMut.mutate(location.id)}
                style={{ ...smallBtn, background: theme.status.danger.dot, color: '#fff', border: 'none' }}>
                Retire
              </button>
            )}
          </>
        ) : (
          <button onClick={() => setConfirmRetire(true)} style={{ ...smallBtn, color: theme.status.danger.fg }}>
            Retire
          </button>
        ))}
      </div>
    </div>
  );
}

function EmptyMsg({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '14px 0', fontSize: 13, color: theme.textMuted, textAlign: 'center' }}>{children}</div>;
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px', background: theme.surface,
  border: `1px solid ${theme.border}`, borderRadius: 8,
  fontSize: 13, color: theme.text, fontFamily: theme.font,
  outline: 'none', boxSizing: 'border-box',
};

const addBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '7px 14px', borderRadius: 8,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const smallBtn: React.CSSProperties = {
  padding: '7px 10px', borderRadius: 8, flexShrink: 0,
  background: theme.surface, border: `1px solid ${theme.border}`,
  color: theme.textDim, fontSize: 12, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const primarySmall: React.CSSProperties = {
  ...smallBtn, background: theme.accent, border: 'none', color: theme.accentInk,
};
